import type { ThemeTokens } from './types';

const palette = {
  black: '#0A0A0A',
  white: '#FFFFFF',
  orange: '#FE7028',
  olive: '#9EC272',
  purple: '#826EC5',
  'mint-green': '#35D487',

  yellow: {
    base: '#F3D743',
    300: '#EAB308',
  },

  green: {
    100: '#DBFEED',
    500: '#4BAC66',
  },

  red: {
    base: '#FF5656',
    100: '#E56862',
    200: '#FD2D40',
  },

  blue: {
    50: '#F2F7FD',
    100: '#DCE6FF',
    200: '#C4DAF3',
    500: '#5697DA',
    700: '#1C4D88',
    800: '#193E6A',
    900: '#0500EA',
  },

  grey: {
    50: '#F6F7F9',
    100: '#ECEEF2',
    150: '#DBDBDB',
    200: '#BFC7DB',
    400: '#8791A9',
    600: '#58627C',
    800: '#3B4151',
  },
} as const;

const semanticTokens: ThemeTokens = {
  primary: palette.blue[700],
  secondary: palette.blue[500],
  accent: palette.blue[800],
  'accent-secondary': palette.blue[500],
  'accent-light': palette.blue[200],
  warning: palette.orange,
  'warning-subtle': palette.yellow.base,
  'success-subtle': palette.olive,
  error: palette.red[200],
  danger: palette.red.base,
  white: palette.white,
  black: palette.black,

  'bg-primary': palette.grey[50],
  'bg-secondary': palette.grey[100],
  'bg-tertiary': palette.grey[200],
  'bg-tertiary-subtle': palette.grey[400],
  'bg-accent': palette.blue[700],
  'bg-accent-secondary': palette.blue[500],
  'bg-accent-tertiary': palette.blue[100],
  'bg-accent-subtle': palette.blue[50],
  'bg-white': palette.white,
  'bg-success': palette.green[100],

  'text-accent': palette.blue[700],
  'text-accent-secondary': palette.blue[900],
  'text-primary': palette.grey[800],
  'text-secondary': palette.grey[600],
  'text-tertiary': palette.grey[400],
  'text-link': palette.blue[500],
  'text-danger': palette.red.base,
  'text-error': palette.red[200],
  'text-on-dark': palette.white,
  'text-warning': palette.orange,
  'text-warning-subtle': palette.yellow[300],
  'text-success': palette.green[500],
  'text-black': palette.black,

  'icon-primary': palette.grey[800],
  'icon-secondary': palette.grey[600],
  'icon-tertiary': palette.grey[400],
  'icon-link': palette.blue[500],
  'icon-accent': palette.blue[700],
  'icon-white': palette.white,
  'icon-error': palette.red[200],
  'icon-success': palette['mint-green'],
  'icon-success-secondary': palette.green[500],

  'border-primary': palette.grey[100],
  'border-secondary': palette.grey[200],
  'border-tertiary': palette.grey[400],
  'border-link': palette.blue[500],
  'border-hover': palette.blue[700],
  'border-focus': palette.blue[700],
  'border-error': palette.red[200],

  'box-shadow-primary': palette.grey[100],
  'box-shadow-secondary': palette.grey[150],
  'box-shadow-tertiary': palette.grey[600],

  'text-field-base': palette.grey[200],

  'button-primary': palette.blue[700],
  'button-secondary': palette.blue[500],
  'button-accent-subtle': palette.blue[50],
  'button-surface-light': palette.grey[400],
  'button-surface': palette.grey[600],
  'button-surface-subtle': palette.grey[800],
  'button-tertiary': palette.grey[200],
  'button-error': palette.red[200],
  'button-danger': palette.red.base,
  'button-success': palette.green[500],

  // Components
  'logo-color': palette.blue[800],
  'header-bg-color': palette.blue[800],
  'header-text-color': palette.white,

  'footer-border-color': palette.grey[600],
  'footer-bg-color': palette.grey[600],
  'footer-text-color': palette.white,
  'footer-divider-color': palette.grey[400],
  'footer-logo-color': palette.white,

  'sidebar-badge-bg-color': palette.blue[200],
  'sidebar-badge-text-color': palette.blue[700],

  'snackbar-info-color': palette.blue[500],
};

export const colors = {
  ...semanticTokens,
};
